import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import LanguageIcon from '@material-ui/icons/Language';
import clsx from 'clsx';
import { cloneElement, isValidElement, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { onBandColor } from '@/styles/darkHero.ts';

import { formatSpokenLanguages } from '@/utils/language-utils.ts';
import type { LanguageEntry } from '@/utils/language-utils.ts';

import { ProfilePicturePlaceholder } from './profile-picture-placeholder.tsx';

const useStyles = makeStyles((theme) => ({
    identity: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        boxSizing: 'border-box',
    },
    identityHero: {
        gap: 8,
        paddingTop: 15,
        [theme.breakpoints.up('sm')]: {
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: 24,
        },
    },
    image: {
        width: '10rem',
        height: '10rem',
        flexShrink: 0,
        objectFit: 'cover',
        borderRadius: '50%',
        border: theme.border.light,
    },
    info: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
    },
    infoHero: {
        [theme.breakpoints.up('sm')]: {
            alignItems: 'flex-start',
            flex: 1,
            paddingBottom: '3em',
        },
    },
    name: {
        ...theme.typography.HL1,
        fontSize: '1.75rem',
        textAlign: 'center',
    },
    nameHero: {
        [theme.breakpoints.up('sm')]: {
            textAlign: 'left',
        },
    },
    nameDark: {
        color: onBandColor(theme),
    },
    profession: {
        textAlign: 'center',
        textTransform: 'none',
    },
    professionHero: {
        [theme.breakpoints.up('sm')]: {
            textAlign: 'left',
        },
    },
    professionDark: {
        color: onBandColor(theme),
    },
    pill: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        padding: '0.1875rem 0.5rem',
        borderRadius: theme.radius.default,
        backgroundColor: theme.palette.common.lightBrown,
        color: theme.palette.common.darkGrey,
        fontSize: '0.75rem',
        textTransform: 'none',
    },
    infoRow: {
        display: 'inline-flex',
        alignItems: 'flex-start',
        gap: '0.25rem',
        color: theme.palette.common.darkGrey,
        fontSize: '0.875rem',
        textAlign: 'center',
    },
    infoRowHero: {
        [theme.breakpoints.up('sm')]: {
            textAlign: 'left',
        },
    },
    infoRowDark: {
        color: onBandColor(theme),
    },
    infoIcon: {
        fontSize: '0.875rem',
        color: theme.palette.common.secondary,
        marginTop: '0.15rem',
    },
    infoIconDark: {
        color: onBandColor(theme),
    },
    rowText: {
        fontSize: 'inherit',
        textTransform: 'none',
    },
}));

interface ProfessionalInfoRow {
    icon: ReactNode;
    text?: string | null;
}

interface Props {
    variant?: 'hero' | 'card';
    dark?: boolean;
    imageUrl?: string | null;
    imageAlt?: string;
    name?: string | null;
    professionText?: string | null;
    practiceName?: string | null;
    languages?: LanguageEntry[] | null;
    infoRows?: ProfessionalInfoRow[];
    className?: string;
    children?: ReactNode;
}

const ProfessionalIdentityInfo = ({
    variant = 'hero',
    dark = false,
    imageUrl,
    imageAlt = '',
    name,
    professionText,
    practiceName,
    languages,
    infoRows = [],
    className,
    children,
}: Props) => {
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const isHero = variant === 'hero';

    const spokenLanguages = useMemo(
        () => (languages ? formatSpokenLanguages(languages, i18n.language, t) : null),
        [languages, i18n.language, t]
    );

    const rows: ProfessionalInfoRow[] = [...infoRows];
    if (spokenLanguages) {
        rows.push({
            icon: <LanguageIcon />,
            text: spokenLanguages,
        });
    }

    return (
        <div className={clsx(classes.identity, isHero && classes.identityHero)}>
            {imageUrl !== undefined &&
                (imageUrl ? (
                    <img className={classes.image} src={imageUrl} alt={imageAlt} />
                ) : (
                    <ProfilePicturePlaceholder className={classes.image} />
                ))}
            <div className={clsx(classes.info, isHero && classes.infoHero, className)}>
                {name && (
                    <Typography className={clsx(classes.name, isHero && classes.nameHero, dark && classes.nameDark)}>
                        {name}
                    </Typography>
                )}
                {professionText && (
                    <Typography
                        className={clsx(
                            classes.profession,
                            isHero && classes.professionHero,
                            dark && classes.professionDark
                        )}
                    >
                        {professionText}
                    </Typography>
                )}
                {practiceName && (
                    <div className={classes.pill}>
                        <Typography className={classes.rowText}>{practiceName}</Typography>
                    </div>
                )}
                {rows
                    .filter((row) => Boolean(row.text))
                    .map((row) => (
                        <div
                            key={row.text}
                            className={clsx(
                                classes.infoRow,
                                isHero && classes.infoRowHero,
                                dark && classes.infoRowDark
                            )}
                        >
                            {isValidElement<{ className?: string }>(row.icon)
                                ? cloneElement(row.icon, {
                                      className: clsx(
                                          classes.infoIcon,
                                          dark && classes.infoIconDark,
                                          row.icon.props.className
                                      ),
                                  })
                                : row.icon}
                            <Typography className={classes.rowText}>{row.text}</Typography>
                        </div>
                    ))}
                {children}
            </div>
        </div>
    );
};

export default ProfessionalIdentityInfo;
